import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { redirectToCheckout, redirectToPortal } from '../../services/stripeService';
import { FREE_TIER_ASSISTANT_REQUESTS_LIMIT, FREE_TIER_SIMULATION_LIMIT } from '../../config/constants';

export type SubscriptionTier = 'free' | 'pro' | 'team';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | null;

interface SubscriptionProfile {
    subscription_tier: SubscriptionTier;
    subscription_status: SubscriptionStatus;
    assistant_requests_count: number;
    subscription_period_end: string | null;
    trial_ends_at: string | null;
    stripe_customer_id: string | null;
    is_admin: boolean;
}

interface SubscriptionContextType {
    subscriptionTier: SubscriptionTier;
    subscriptionStatus: SubscriptionStatus;
    assistantRequestsCount: number;
    subscriptionPeriodEnd: string | null;
    trialEndsAt: string | null;
    loading: boolean;
    hasStripeCustomer: boolean;
    isAdmin: boolean;
    canCreateSimulation: (currentCount: number, hasExample: boolean) => boolean;
    canUseAssistant: () => boolean;
    remainingAssistantRequests: () => number | 'unlimited';
    isReadOnly: (simulationCount: number) => boolean;
    upgradeToPro: (annual: boolean) => Promise<void>;
    manageSubscription: (returnUrl?: string) => Promise<void>;
    refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
    const { user } = useAuth();
    const [profile, setProfile] = useState<SubscriptionProfile | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchSubscription = useCallback(async () => {
        if (!user) {
            setProfile(null);
            setLoading(false);
            return;
        }

        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('subscription_tier, subscription_status, assistant_requests_count, subscription_period_end, trial_ends_at, stripe_customer_id, is_admin')
                .eq('id', user.id)
                .single();

            if (error) {
                // Handle network errors (CORS, 521, etc.) gracefully
                const isNetworkError = 
                    error.message?.includes('NetworkError') ||
                    error.message?.includes('fetch') ||
                    error.message?.includes('CORS') ||
                    error.code === 'PGRST301' || // Supabase network error code
                    error.message?.includes('Failed to fetch');
                
                if (isNetworkError) {
                    // Network errors are often temporary (server down, CORS issues, etc.)
                    // Log in dev mode only, silently handle in production
                    if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
                        console.warn('[SubscriptionContext] Network error fetching subscription (will retry):', error.message);
                    }
                } else {
                    // Other errors (auth, permissions, etc.) should be logged
                    console.error('[SubscriptionContext] Error fetching subscription:', error);
                }
                
                // Default to free tier on error - app continues to work
                setProfile({
                    subscription_tier: 'free',
                    subscription_status: null,
                    assistant_requests_count: 0,
                    subscription_period_end: null,
                    trial_ends_at: null,
                    stripe_customer_id: null,
                    is_admin: false,
                });
            } else {
                setProfile(data as SubscriptionProfile);
            }
        } catch (error) {
            // Handle unexpected errors (TypeError, etc.)
            const err = error as Error;
            const isNetworkError = 
                err.message?.includes('NetworkError') ||
                err.message?.includes('fetch') ||
                err.message?.includes('CORS') ||
                err.name === 'TypeError';
            
            if (isNetworkError) {
                if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
                    console.warn('[SubscriptionContext] Network error (will retry):', err.message);
                }
            } else {
                console.error('[SubscriptionContext] Unexpected error fetching subscription:', error);
            }
            
            // Default to free tier on error
            setProfile({
                subscription_tier: 'free',
                subscription_status: null,
                assistant_requests_count: 0,
                subscription_period_end: null,
                trial_ends_at: null,
                stripe_customer_id: null,
                is_admin: false,
            });
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    // Subscribe to realtime changes
    useEffect(() => {
        if (!user) return;

        const channel = supabase
            .channel('profile-changes')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'profiles',
                    filter: `id=eq.${user.id}`,
                },
                () => {
                    fetchSubscription();
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user, fetchSubscription]);

    const canCreateSimulation = useCallback((currentCount: number, hasExample: boolean): boolean => {
        // Admins have unlimited simulations
        if (profile?.is_admin) {
            return true;
        }
        
        const tier = profile?.subscription_tier || 'free';
        
        // Pro and Team have unlimited simulations
        if (tier === 'pro' || tier === 'team') {
            return true;
        }
        
        // Free tier: only 1 simulation (excluding example)
        const nonExampleCount = hasExample ? currentCount - 1 : currentCount;
        return nonExampleCount < FREE_TIER_SIMULATION_LIMIT;
    }, [profile]);

    const canUseAssistant = useCallback((): boolean => {
        // Admins have unlimited assistant requests
        if (profile?.is_admin) {
            return true;
        }
        
        const tier = profile?.subscription_tier || 'free';
        
        // Pro and Team have unlimited assistant requests
        if (tier === 'pro' || tier === 'team') {
            return true;
        }
        
        // Free tier: limit of requests
        return (profile?.assistant_requests_count || 0) < FREE_TIER_ASSISTANT_REQUESTS_LIMIT;
    }, [profile]);

    const remainingAssistantRequests = useCallback((): number | 'unlimited' => {
        // Admins have unlimited assistant requests
        if (profile?.is_admin) {
            return 'unlimited';
        }
        
        const tier = profile?.subscription_tier || 'free';
        
        if (tier === 'pro' || tier === 'team') {
            return 'unlimited';
        }
        
        return Math.max(0, FREE_TIER_ASSISTANT_REQUESTS_LIMIT - (profile?.assistant_requests_count || 0));
    }, [profile]);

    const isReadOnly = useCallback((simulationCount: number): boolean => {
        // Admins are never in read-only mode
        if (profile?.is_admin) {
            return false;
        }
        
        const tier = profile?.subscription_tier || 'free';
        
        // Only free tier users with more than the free tier limit are in read-only mode
        // This happens when they downgrade from a paid plan
        if (tier === 'free' && simulationCount > FREE_TIER_SIMULATION_LIMIT) {
            return true;
        }
        
        return false;
    }, [profile]);

    const upgradeToPro = useCallback(async (annual: boolean): Promise<void> => {
        const priceId = annual 
            ? import.meta.env.VITE_STRIPE_PRO_ANNUAL_PRICE_ID
            : import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID;
        
        if (!priceId) {
            throw new Error('Stripe price ID not configured');
        }
        
        await redirectToCheckout(priceId);
    }, []);

    const manageSubscription = useCallback(async (returnUrl?: string): Promise<void> => {
        await redirectToPortal(returnUrl || '/workspace');
    }, []);

    return (
        <SubscriptionContext.Provider
            value={{
                subscriptionTier: profile?.subscription_tier || 'free',
                subscriptionStatus: profile?.subscription_status || null,
                assistantRequestsCount: profile?.assistant_requests_count || 0,
                subscriptionPeriodEnd: profile?.subscription_period_end || null,
                trialEndsAt: profile?.trial_ends_at || null,
                loading,
                hasStripeCustomer: !!profile?.stripe_customer_id,
                isAdmin: profile?.is_admin || false,
                canCreateSimulation,
                canUseAssistant,
                remainingAssistantRequests,
                isReadOnly,
                upgradeToPro,
                manageSubscription,
                refreshSubscription: fetchSubscription,
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};
